import { useState, useMemo } from 'react';
import { Row, Col, Card, Form, Spinner, Button } from 'react-bootstrap';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import PageBreadcrumb from '../../components/common/PageBreadcrumb';
import AlertNotificationItem from '../../components/alerts/AlertNotificationItem';
import { useAlerts, useAckAlert } from '../../features/alerts/useAlerts';
import { useAuthContext } from '../../providers/AuthProvider';
import { MACHINES as MACHINE_NAMES } from '../../data/operatorConstants.js';

const MACHINES = MACHINE_NAMES.map(m => m.toLowerCase());

const breadcrumbItems = [
  { label: 'Machine Management', url: '/apps/machine-management/machine-tracking' },
  { label: 'Notifications', active: true }
];

const SEVERITIES = ['CRITICAL', 'WARNING', 'INFO'];

/**
 * Split by the day the problem STARTED, not the day it was last checked.
 *
 * A line that went down at 23:40 and is still down at 00:10 belongs under
 * yesterday — that is where somebody looking for "when did this begin" will go,
 * and it keeps a long-running fault from silently jumping days while nothing
 * about it changed.
 */
const groupByDay = alerts => {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');
  const groups = { Today: [], Yesterday: [], Earlier: [] };

  for (const a of alerts) {
    const d = a.firstSeenAt ? dayjs(Number(a.firstSeenAt)) : null;
    if (d && d.isAfter(today)) groups.Today.push(a);
    else if (d && d.isAfter(yesterday)) groups.Yesterday.push(a);
    else groups.Earlier.push(a);
  }
  return groups;
};

/**
 * The factory's notification feed.
 *
 * Deliberately a feed rather than a table: these are things that happened to
 * machines over a day, and reading them in time order is how somebody picks up
 * a shift. The analytical view — which rules fire most, how much time they
 * account for — lives on the Factory Alerts page.
 */
const AlertNotifications = () => {
  const [machineId, setMachineId] = useState('');
  const [severity, setSeverity] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [authState] = useAuthContext();

  const filters = useMemo(
    () => ({
      status: showResolved ? 'ALL' : 'OPEN,ACKNOWLEDGED',
      ...(machineId ? { machineId } : {}),
      ...(severity ? { severity } : {}),
      limit: 200
    }),
    [machineId, severity, showResolved]
  );

  const { data, isLoading } = useAlerts(filters);
  const ack = useAckAlert();

  const alerts = useMemo(() => data?.data || [], [data]);
  const counts = data?.counts || {};
  const groups = useMemo(() => groupByDay(alerts), [alerts]);

  const actor =
    authState?.user?.name || authState?.user?.username || null;
  const onAck = alert => ack.mutate({ id: alert._id, actor });

  const pending = alerts.filter(a => a.status === 'OPEN');
  const ackAll = () => pending.forEach(a => ack.mutate({ id: a._id, actor }));

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <h2 className="mb-1">Notifications</h2>
          <p className="text-body-tertiary mb-0 fs-9">
            {counts.CRITICAL || 0} critical · {counts.WARNING || 0} warnings ·{' '}
            {counts.INFO || 0} info — these open and close on their own.
          </p>
        </div>
        {pending.length ? (
          <Button variant="phoenix-secondary" size="sm" onClick={ackAll}>
            <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
            Acknowledge all ({pending.length})
          </Button>
        ) : null}
      </div>

      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="mb-1 fs-9 text-body-tertiary">Machine</Form.Label>
              <Form.Select
                size="sm"
                value={machineId}
                onChange={e => setMachineId(e.target.value)}
              >
                <option value="">All machines</option>
                {MACHINES.map(m => (
                  <option key={m} value={m}>
                    {m.toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="mb-1 fs-9 text-body-tertiary">Severity</Form.Label>
              <Form.Select
                size="sm"
                value={severity}
                onChange={e => setSeverity(e.target.value)}
              >
                <option value="">All severities</option>
                {SEVERITIES.map(s => (
                  <option key={s} value={s}>
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="notif-show-resolved"
                label="Include resolved"
                checked={showResolved}
                onChange={e => setShowResolved(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-8 gap-2">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="fs-9 text-body-tertiary">Loading…</span>
        </div>
      ) : !alerts.length ? (
        <Card>
          <Card.Body className="text-center py-8">
            <h5 className="text-body-emphasis mb-1">Nothing needs attention</h5>
            <p className="text-body-tertiary fs-9 mb-0">
              Every machine is either running or accounted for.
            </p>
          </Card.Body>
        </Card>
      ) : (
        Object.entries(groups)
          .filter(([, list]) => list.length)
          .map(([label, list]) => (
            <div key={label}>
              <h5 className="text-body-emphasis mb-3">
                {label}
                <span className="ms-2 fw-normal text-body-tertiary fs-9">
                  {list.length}
                </span>
              </h5>
              <div className="mx-n4 mx-lg-n6 mb-5 border-top">
                {list.map((alert, index) => (
                  <AlertNotificationItem
                    key={alert._id}
                    alert={alert}
                    type="pageItem"
                    onAck={onAck}
                    className={classNames({
                      'border-bottom': index !== list.length - 1
                    })}
                  />
                ))}
              </div>
            </div>
          ))
      )}
    </div>
  );
};

export default AlertNotifications;
